<template>
    <div class="app-modal-dialog--lite-publication-export app-modal-dialog">
        <div class="app-modal-dialog__content">
            <div class="app-modal-dialog__header">
                <h4 class="app-modal-dialog__title">
                    <span class="app-modal-dialog__subtitle">{{ publication.name }}</span>
                    Export to {{ overlayTitle }} 
                </h4>
                <span v-if="hasDocument" class="element-badge is--info">
                    Generated on {{ getDocumentDate }}
                </span>
            </div>
            <form
                @submit.prevent="submit()"
                autocomplete="off">
                <div class="app-modal-dialog__body grid">
                    <p class="col-12">Generate an export of this course with any selected options. You can download it after generation, and regenerate with different export options at any time.</p>
                    <div class="col-12">
                        <fieldset>
                            <legend v-if="hasExportOptions" class="checkbox__group-label">Export Options</legend>
                            <div v-for="(item, key) in checkboxOptions" :key="key" class="input checkbox">
                                <label class="checkbox__label">
                                    <input type="checkbox" class="checkbox__input" v-model="selectedOptions[key]" :name="`options[${key}]`" :id="key" :checked="item" :title="getOptionLabels(key).description">
                                    <span>
                                        {{ getOptionLabels(key).label }}
                                        <span class="checkbox__supporting-text">{{ getOptionLabels(key).description }}</span>
                                    </span>
                                </label>   
                            </div>
                            <div v-for="(item, key) in dropdownOptions" :key="key" class="input select">
                                <label>{{ getOptionLabels(key).label }}
                                    <span data-bs-toggle="dropdown" >
                                        <svg class="element-icon--tooltip-help element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-tooltip-help"></use></svg>
                                        <div class="app-tooltip dropdown-menu">
                                            <h4 class="app-tooltip__title">{{ getOptionLabels(key).label }}</h4>
                                            <div class="app-tooltip__body">
                                                <p>{{ getOptionLabels(key).description }}</p>
                                            </div>
                                        </div>
                                    </span>
                                </label>
                                <select v-model="selectedOptions[key]">
                                    <option v-for="(label, item) in dropdownOptions[key]" :value="item">{{ label }}</option>
                                </select>
                            </div>
                        </fieldset>
                    </div>
                    <div class="col-12" v-show="exportType === 'ppt'">
                        <template v-if="hasThemeSelect">
                            <fieldset class="input select">
                                <label >{{ exportType.toUpperCase() }} Template 
                                </label> 
                                <select v-model="themeTemplate" @change="onTemplateSelect(themeTemplate)">
                                    <option v-for="(label, item) in themeOptions" :value="item">{{ getThemeLabel(item) }}</option>
                                </select>
                            </fieldset>
                            <p class="app-modal-dialog__ppt-templates inline-message">
                                Not seeing the template you're looking for?
                                <a class="element-link" title="Manage PPT templates for this project here."
                                :href="`/projects/${publication.project_id}/template?export-ppt=${publication.id}`">
                                Manage PPT templates for this project here.
                                </a>
                            </p>
                        </template>
                        <template v-else>
                            <p>No available themes for {{ exportType.toUpperCase() }} export. Please contact support to update Driver version for supported templates.</p>
                        </template>
                    </div>
                </div>
                <div class="app-modal-dialog__footer">
                    <p v-if="hasDocument && outOfDate" class="inline-message is--warning">
                        This generated file is out-of-date. Select the Regenerate button to generate a file with the latest course updates.
                    </p>
                    <div class="app-modal-dialog__footer-buttons">
                        <app-button
                            theme="danger"
                            class="element-button--secondary element-button"
                            @click="cancel()">
                            Cancel
                        </app-button>
                        <app-button v-if="((hasChanges && !disabled) || !hasDocument || outOfDate) && (hasThemeSelect || exportType !== 'ppt')"
                            theme="primary"
                            :class="[hasDocument && !hasChanges ? 'element-button--secondary' : 'element-button--primary', 'element-button']"
                            @click="generate()">
                            {{ disabled ? 'Generating...' : (hasDocument ? `Regenerate ${overlayTitle}` : `Generate ${overlayTitle}`) }}
                        </app-button>
                        <app-button v-if="(hasDocument && (!hasChanges || !hasThemeSelect))"
                            theme="primary"
                            class="element-button--primary element-button"
                            @click="downloadDoc()">
                            Download
                        </app-button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import _ from 'lodash'
import api from '../api/publisher'
import { hasDocument } from '@microsoft/applicationinsights-core-js'
import { localizeDate } from '../helpers/util'

export default {
    props: {
        source: {
            type: Object,
            default: () => ({
                id: 'new-publication',
                name: '',
                description: '',
                options: {},
            })
        },
        exportOptions: {
            type: Object,
        },
        exportType: {
            type: String,
        },
        publication: {
            type: Object,
        },
    },
    data () {
        const clonedSource = _.cloneDeep(this.source)
        return {
            options: _.cloneDeep(this.exportOptions),
            themeTemplate: 0,
            disabled: false,
            selectedOptions: {},
            selectedDefaults: {},
        }
    },
    computed: {
        requirementsMet() {
            return this.publication.name.length
        },
        hasDocument() {
            var $hasDoc = false;
            if (this.exportType === 'scorm') {
                return this.publication.path;
            }
            _.each(this.publication.doc_exports, ($doc) => {
                if ($doc.file_type === this.docExtension) {
                    $hasDoc = $doc
                    return;
                }
            })
            return $hasDoc;
        },
        outOfDate() {
            if (this.hasDocument) {
                // Compare actual dates rather than formatted strings
                const pubMoment = localizeDate(this.publication.updated_at);
                let docMoment = null;
                if (this.exportType === 'scorm') {
                    docMoment = localizeDate(this.publication.published_at);
                } else {
                    const matching = _.find(this.publication.doc_exports, (d) => d.file_type === this.docExtension);
                    if (matching) {
                        docMoment = localizeDate(matching.updated_at);
                    }
                }
                if (!docMoment || !pubMoment) {
                    return false;
                }
                // Return true if the document is older than the publication (out of date)
                return typeof docMoment.isBefore === 'function'
                    ? docMoment.isBefore(pubMoment)
                    : (new Date(docMoment) < new Date(pubMoment));
            }
            return false;
        },
        hasExportOptions() {
            return !_.isEmpty(this.checkboxOptions) || !_.isEmpty(this.dropdownOptions)
        },
        getDocumentDate() {
            if (this.hasDocument) {
                var $docDate = null;
                if (this.exportType === 'scorm') {
                    $docDate = this.publication.published_at
                }
                else {
                    _.each(this.publication.doc_exports, ($doc) => {
                        if ($doc.file_type === this.docExtension) {
                            $docDate = $doc.updated_at
                            return;
                        }
                    })
                }
                return localizeDate($docDate).format('MMM D, YYYY h:mm A')
            }
            return null
        },
        savedOptions() {
            return _.get(this.publication, `ai_preferences.export_options.${this.exportType}`, {})
        },
        checkboxOptions() {
            return _.pickBy(this.options, (value, key) => {
                return _.isBoolean(value)
            })
        },
        dropdownOptions() {
            return _.pickBy(this.options, (value, key) => {
                return _.isObject(value) && !_.isEmpty(value) && key !== 'template_options'
            })
        },
        themeOptions() {
            return _.get(this.options, 'template_options', [])
        },
        hasThemeSelect() {
            return !_.isEmpty(this.themeOptions)
        },
        hasChanges() {
            return !_.isEqual(this.selectedOptions, this.savedOptions)
        },
        overlayTitle() {
            switch (this.exportType) {
                case 'word':
                    return 'MS Word'
                case 'ppt':
                    return 'MS PowerPoint'
                case 'scorm':
                    return 'SCORM'
                default:
                    return 'NOt VALID'
            }
        },
        docExtension() {
            switch (this.exportType) {
                case 'word':
                    return '.docx'
                case 'ppt':
                    return '.pptx'
                case 'scorm':
                    return '.zip'
                default:
                    return 'NOt VALID'
            }
        },
        pptxTemplate() {
            return this.publication?.ai_preferences?.pptx_template || null
        }
    },
    methods: {
        cancel () {
            this.$emit('cancel')
        },
        setDisabled(value = true) {
            this.disabled = value
        },
        getThemeLabel(value) {
            if (this.themeOptions[value]) {
                return this.themeOptions[value].label
            }
            return value
        },
        getThemePath(value) {
            if (this.themeOptions[value]) {
                let _path = this.themeOptions[value].path
                console.log('getThemePath', value, _path, this.pptxTemplate)
                return _path
            }
            return value
        },
        getThemeFromPath(path) {
            let foundKey = null
            _.each(this.themeOptions, (item, key) => {
                if (item.path === path) {
                    foundKey = key
                    return false
                }
            })
            return foundKey
        },
        onTemplateSelect(value) {
            this.selectedOptions.templateName = this.getThemePath(this.themeTemplate)
        },
        getOptionLabels(_optionKey) {
            switch (_optionKey) {
                case 'show_title_page':
                    return {
                        label: 'Insert a title page',
                        description: 'Added to the beginning of the document.'
                    }
                case 'show_toc':
                    return {
                        label: 'Insert a table of contents',
                        description: 'Added to the beginning of the document.'
                    }
                case 'show_title_blocks':
                    return {
                        label: 'Include topic and learning object names',
                        description: 'Added to the beginning of each section.'
                    }
                case 'show_header_blocks':
                    return {
                        label: 'Include complex screen header text',
                        description: 'Added to the beginning of complex screens.'
                    }
                case 'show_title_numbers':
                    return {
                        label: 'Automatically number each section',
                        description: 'Added to topics (1, 2, etc.), learning objects (1.1, 1.2, etc.), and screens (1.1.1, 1.1.2, etc.).'
                    }
                case 'show_presenter_notes':
                    return {
                        label: 'Include AI-generated Notes on each slide',
                        description: 'Selecting this option may significantly increase generation time.'
                    }
                case 'asset_display_method':
                    return {
                        label: 'Image Display Method',
                        description: 'Choose how images are displayed in the generated document. "Crop" will crop images to fill the placeholder while maintaining aspect ratio, "Stretch" will stretch images to fit the placeholder, and "Fit" will fit images within the placeholder while maintaining aspect ratio.'
                    }
                default:
                    return this.exportOptions[_optionKey]
            }
        },
        async generate() {
            let generateOptions = _.cloneDeep(this.options)
            this.setDisabled(true)
            
            if (this.hasThemeSelect)
                generateOptions.templateName = this.themeOptions[this.themeTemplate].path
        
            _.each(this.selectedOptions, (value, key) => {
                generateOptions[key] = value
            })
            
            const data = { 'options': generateOptions }
            var result = null;
            
            console.info('Generating document export...', generateOptions)
            if (this.exportType === 'scorm')
                result = await api.publish(this.publication.id, data)
            else
                result = await api.generateDoc(this.publication.id, data, this.exportType)
        
            if ((result && result.success) || (result && result.id === this.publication.id))
            {
                this.setDisabled(false)
                this.$emit('docGenerated', this.publication.id)
                if (result.warnings && result.warnings.length > 0) {
                    alert('Document generated with warnings: ' + result.warnings.join(', ') + '\n\nDocument may be missing some content. Please review the document and regenerate if necessary.')
                }
            }
            else
            {
                console.error('Error generating document', result)
                this.setDisabled(false)
                alert('Error generating document: ' + (result.message || 'Unknown error'))
            }
        },
        downloadDoc() {
            console.log('Downloading document export...')
            if (this.exportType === 'scorm')
                window.open(`/publications/${this.publication.id}/download`, '_blank');
            else
                window.open(`/publications/${this.publication.id}/download-document/${this.hasDocument.id}`, '_blank');
        }
    },
    created() {
        this.selectedDefaults = _.mapValues(this.options, (so, key) => {
            if (key == 'template_options') {
                return this.themeOptions[0] ? this.themeOptions[0].path : null
            } else if (_.isObject(so) && !_.isEmpty(so)) {
                return _.keys(so)[0]
            } else if (_.isBoolean(so)) {
                return so
            }
            return so
        })
        // initialize selected options with saved options or defaults, but only for keys that are in the current options
        this.selectedOptions = _.mapValues(this.options, (value, key) => {
            if (key === 'template_options') {
                key = 'templateName'
            }
            if (this.savedOptions.hasOwnProperty(key)) {
                return this.savedOptions[key]
            } else if (this.selectedDefaults.hasOwnProperty(key)) {
                return this.selectedDefaults[key]
            }
            return value
        })
        if (this.exportType === 'ppt') {
            this.themeTemplate = this.getThemeFromPath(this.savedOptions?.templateName) || 0
            this.selectedOptions.templateName = this.getThemePath(this.themeTemplate)
            delete this.selectedOptions.template_options
        }
    },
    mounted() {
        console.log('mounted themeTemplate', this.themeTemplate, this.options, this.selectedDefaults, this.savedOptions, this.selectedOptions)
    },
}
</script>
